import React, { useEffect, useState, useRef } from 'react';
import { buildApiUrl } from '../config/api';

export default function Embed({ url, className }) {
  const [html, setHtml] = useState(null);
  const [provider, setProvider] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return;
    let mounted = true;
    setHtml(null);
    setError(null);
    const endpoint = `${buildApiUrl('/api/embed')}?url=${encodeURIComponent(
      url
    )}`;
    (async () => {
      try {
        const r = await fetch(endpoint);
        if (!mounted) return;
        if (!r.ok) {
          const t = await r.text().catch(() => '');
          throw new Error(t || `HTTP ${r.status}`);
        }

        // Read as text first and attempt to parse JSON (robust against wrong content-type)
        const text = await r.text();
        if (!mounted) return;
        let parsed = null;
        try {
          parsed = JSON.parse(text);
        } catch (e) {
          // Not JSON — treat as raw HTML response
          parsed = { __rawText: text };
        }

        if (!parsed) {
          setError('Empty response');
          return;
        }
        if (parsed.__rawText) {
          // Backend returned raw HTML (not JSON). Treat it as sanitized embed HTML and render it.
          const raw = parsed.__rawText;
          // Heuristically detect provider from returned HTML
          let detectedProvider = null;
          if (/twitter\.com|platform\.twitter\.com|twttr/i.test(raw) || /class="twitter-|data-tweet-id|twitter-tweet/.test(raw)) {
            detectedProvider = 'x';
          } else if (/instagram\.com|instgrm/i.test(raw) || /class="instagram-media|instagram-media"/.test(raw)) {
            detectedProvider = 'instagram';
          }
          setHtml(raw);
          setProvider(detectedProvider);
          return;
        }
        if (parsed.error) {
          setError(parsed.error);
          return;
        }

        setHtml(parsed.html || '');
        setProvider(parsed.provider || null);
      } catch (err) {
        if (!mounted) return;
        console.error('Embed fetch error', err);
        setError('Failed to load embed');
      }
    })();

    return () => {
      mounted = false;
    };
  }, [url]);

  const containerRef = useRef(null);

  // Load X/Twitter widget script once when provider is x and re-run widgets.load on content changes
  useEffect(() => {
    if (provider !== 'x') return;
    const id = 'twitter-wjs';
    let scriptAdded = false;
    const ensureLoad = () => {
      try {
        if (window && window.twttr && window.twttr.widgets && typeof window.twttr.widgets.load === 'function') {
          // load only the container to avoid reprocessing entire document repeatedly
          window.twttr.widgets.load(containerRef.current || document.body);
        }
      } catch (e) {
        // ignore
      }
    };

    if (!document.getElementById(id)) {
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://platform.twitter.com/widgets.js';
      s.async = true;
      s.onload = () => {
        ensureLoad();
      };
      document.body.appendChild(s);
      scriptAdded = true;
    } else {
      // script already present — ensure widgets are (re)initialized
      ensureLoad();
    }

    // Also attempt to call widgets.load whenever html changes (via separate effect)
    return () => {
      // do not remove script on unmount since other embeds may rely on it
      scriptAdded = false;
    };
  }, [provider]);

  // Re-run widgets.load each time html changes so client-side navigation renders embeds
  useEffect(() => {
    if (!html) return;
    try {
      if (provider === 'x') {
        if (window && window.twttr && window.twttr.widgets && typeof window.twttr.widgets.load === 'function') {
          window.twttr.widgets.load(containerRef.current || document.body);
        }
      }
      if (provider === 'instagram') {
        if (window && window.instgrm && typeof window.instgrm.Embeds === 'object' && typeof window.instgrm.Embeds.process === 'function') {
          // The Instagram embed script exposes instgrm.Embeds.process()
          window.instgrm.Embeds.process();
        }
      }
    } catch (e) {
      // ignore
    }
  }, [html, provider]);

  // Load Instagram embed script when provider is instagram
  useEffect(() => {
    if (provider !== 'instagram') return;
    const id = 'instagram-embed-js';
    if (!document.getElementById(id)) {
      const s = document.createElement('script');
      s.id = id;
      s.src = 'https://www.instagram.com/embed.js';
      s.async = true;
      s.onload = () => {
        try {
          if (window && window.instgrm && typeof window.instgrm.Embeds.process === 'function') {
            window.instgrm.Embeds.process();
          }
        } catch (e) {
          // ignore
        }
      };
      document.body.appendChild(s);
    } else {
      try {
        if (window && window.instgrm && typeof window.instgrm.Embeds.process === 'function') {
          window.instgrm.Embeds.process();
        }
      } catch (e) {
        // ignore
      }
    }
  }, [provider]);

  if (!url) return null;
  if (error) return <div className={className}>Failed to load embed: {error}</div>;
  if (html === null) return <div className={className}>Loading embed…</div>;

  return (
    <div
      ref={containerRef}
      className={className}
      // server already sanitizes; this is safe when server sanitize is trusted
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
